import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiService } from '@/services/apiService';
import { 
  Building2, 
  User, 
  BookOpen, 
  ArrowRight, 
  ArrowLeft,
  Check,
  Loader2,
  Briefcase,
  Users,
  FileText,
  Search,
  Linkedin
} from 'lucide-react';

// Get API base URL for LinkedIn OAuth redirect
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

type RegistrationStep = 'account' | 'intent' | 'profile' | 'success';
type UserIntent = 'hire' | 'cxo' | 'learn' | null;

interface ProfileFormData {
  companyName?: string;
  industry?: string;
  companySize?: string;
  title?: string;
  executiveFunction?: string;
  yearsOfExperience?: string;
  hourlyRateMin?: string;
  hourlyRateMax?: string;
}

export default function NewRegistrationPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<RegistrationStep>('account');
  const [intent, setIntent] = useState<UserIntent>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Account form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Profile form
  const [profileData, setProfileData] = useState<ProfileFormData>({});
  
  // Created user data
  const [createdUserId, setCreatedUserId] = useState('');

  const validateAccountForm = () => {
    if (!firstName.trim()) return 'First name is required';
    if (!lastName.trim()) return 'Last name is required';
    if (!email.trim()) return 'Email is required';
    if (!password.trim()) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    return '';
  };

  const handleCreateAccount = async () => {
    const validationError = validateAccountForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setError('');
    setStep('intent');
  };

  const handleSelectIntent = (selectedIntent: UserIntent) => {
    setIntent(selectedIntent);
    if (selectedIntent === 'learn') {
      // Skip profile setup for members, create account directly
      handleCompleteRegistration(selectedIntent, null);
    } else {
      setStep('profile');
    }
  };

  const handleCompleteRegistration = async (
    userIntent: UserIntent, 
    profile: ProfileFormData | null
  ) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Determine primary role based on intent
      let primaryRole: 'company' | 'cxo' | 'member' = 'member';
      if (userIntent === 'hire') primaryRole = 'company';
      if (userIntent === 'cxo') primaryRole = 'cxo';
      
      // Create account with profile data
      const response = await apiService.register({
        firstName,
        lastName,
        email,
        password,
        role: primaryRole,
        intent: userIntent || undefined,
        profile: profile || undefined
      });
      
      if (response.success) {
        setCreatedUserId(response.data?.user?.id || '');
        setStep('success');
      } else {
        setError(response.message || 'Registration failed');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = () => {
    // Validate profile data based on intent
    if (intent === 'hire') {
      if (!profileData.companyName?.trim()) {
        setError('Company name is required');
        return;
      }
    } else if (intent === 'cxo') {
      if (!profileData.title?.trim()) {
        setError('Professional title is required');
        return;
      }
    }
    
    setError('');
    handleCompleteRegistration(intent, profileData);
  };

  const handleSkipProfile = () => {
    handleCompleteRegistration(intent, null);
  };

  // Step 1: Account Creation
  if (step === 'account') {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 bg-teal rounded-xl flex items-center justify-center">
                <span className="font-heading text-2xl font-bold text-navy">C</span>
              </div>
              <span className="font-heading text-2xl font-bold text-white">cxood</span>
            </Link>
          </div>

          <div className="bg-navy-light rounded-2xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                Create your account
              </h1>
              <p className="text-slate">
                Start your journey with CxO On Demand
              </p>
            </div>

            {/* Social Login */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <Button 
                variant="outline" 
                className="w-full border-white/10 hover:bg-white/5"
                onClick={() => window.location.href = `${API_BASE_URL}/auth/linkedin`}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </Button>
              <Button 
                variant="outline" 
                className="w-full border-white/10 hover:bg-white/5 relative overflow-hidden"
                disabled
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
                <span className="absolute top-0 right-0 bg-slate/80 text-[10px] px-1.5 py-0.5 rounded-bl text-white font-medium">
                  Soon
                </span>
              </Button>
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-navy-light text-slate">Or with email</span>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-slate">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 bg-navy border-white/10 text-white"
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-slate">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 bg-navy border-white/10 text-white"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-slate">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 bg-navy border-white/10 text-white"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-slate">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 bg-navy border-white/10 text-white"
                  placeholder="Create a secure password"
                />
                <p className="mt-1 text-xs text-slate">
                  Must be at least 8 characters
                </p>
              </div>

              <Button 
                onClick={handleCreateAccount}
                className="w-full bg-teal hover:bg-teal-light text-navy"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <p className="mt-6 text-center text-sm text-slate">
              Already have an account?{' '}
              <Link to="/login" className="text-teal hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Intent Selection
  if (step === 'intent') {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {/* Progress */}
          <div className="flex items-center gap-2 mb-8">
            <button 
              onClick={() => setStep('account')}
              className="flex items-center gap-2 text-slate hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex-1 flex gap-2">
              <div className="h-1 flex-1 bg-teal rounded-full" />
              <div className="h-1 flex-1 bg-teal rounded-full" />
              <div className="h-1 flex-1 bg-white/10 rounded-full" />
            </div>
          </div>

          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-white mb-3">
              Welcome, {firstName}!
            </h1>
            <p className="text-slate text-lg">
              What brings you to CxO On Demand?
            </p>
            <p className="text-slate text-sm mt-2">
              (You can change this later in your settings)
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Hire CXOs */}
            <button
              onClick={() => handleSelectIntent('hire')}
              className="group p-6 bg-navy-light rounded-2xl border-2 border-white/10 hover:border-teal transition-all text-left"
            >
              <div className="w-14 h-14 bg-success/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-success/30 transition-colors">
                <Building2 className="w-7 h-7 text-success" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white mb-2">
                I need to hire CXOs
              </h3>
              <p className="text-sm text-slate">
                Find fractional executives for my company
              </p>
              <ul className="mt-4 space-y-2 text-xs text-slate">
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-success" />
                  Post briefs
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-success" />
                  Browse CXO profiles
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-success" />
                  Manage engagements
                </li>
              </ul>
            </button>

            {/* I am a CXO */}
            <button
              onClick={() => handleSelectIntent('cxo')}
              className="group p-6 bg-navy-light rounded-2xl border-2 border-white/10 hover:border-teal transition-all text-left"
            >
              <div className="w-14 h-14 bg-teal/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal/30 transition-colors">
                <User className="w-7 h-7 text-teal" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white mb-2">
                I am a CXO
              </h3>
              <p className="text-sm text-slate">
                Offer my services as a fractional executive
              </p>
              <ul className="mt-4 space-y-2 text-xs text-slate">
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-teal" />
                  Create CXO profile
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-teal" />
                  Receive brief matches
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-teal" />
                  Manage proposals
                </li>
              </ul>
            </button>

            {/* I want to learn */}
            <button
              onClick={() => handleSelectIntent('learn')}
              className="group p-6 bg-navy-light rounded-2xl border-2 border-white/10 hover:border-teal transition-all text-left"
            >
              <div className="w-14 h-14 bg-slate/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-slate/30 transition-colors">
                <BookOpen className="w-7 h-7 text-slate" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white mb-2">
                I want to learn
              </h3>
              <p className="text-sm text-slate">
                Read articles and follow CXO thought leaders
              </p>
              <ul className="mt-4 space-y-2 text-xs text-slate">
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-slate" />
                  Curated articles
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-slate" />
                  Follow CXOs
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-slate" />
                  Comment & engage
                </li>
              </ul>
            </button>
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => handleSelectIntent('learn')}
              className="text-slate hover:text-white text-sm"
            >
              I'll decide later →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Profile Setup
  if (step === 'profile' && intent) {
    const isCompany = intent === 'hire';
    
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          {/* Progress */}
          <div className="flex items-center gap-2 mb-8">
            <button 
              onClick={() => setStep('intent')}
              className="flex items-center gap-2 text-slate hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex-1 flex gap-2">
              <div className="h-1 flex-1 bg-teal rounded-full" />
              <div className="h-1 flex-1 bg-teal rounded-full" />
              <div className="h-1 flex-1 bg-teal rounded-full" />
            </div>
          </div>

          <div className="bg-navy-light rounded-2xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-teal/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                {isCompany ? (
                  <Building2 className="w-8 h-8 text-success" />
                ) : (
                  <User className="w-8 h-8 text-teal" />
                )}
              </div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                {isCompany ? 'Tell us about your company' : 'Set up your CXO profile'}
              </h1>
              <p className="text-slate">
                {isCompany 
                  ? 'Required to post briefs and hire CXOs' 
                  : 'Required to receive brief matches and leads'}
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {isCompany ? (
                <>
                  <div>
                    <Label htmlFor="companyName" className="text-slate">
                      Company Name *
                    </Label>
                    <Input
                      id="companyName"
                      value={profileData.companyName || ''}
                      onChange={(e) => setProfileData({...profileData, companyName: e.target.value})}
                      className="mt-1 bg-navy border-white/10 text-white"
                      placeholder="Acme Inc."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="industry" className="text-slate">Industry</Label>
                      <select
                        id="industry"
                        value={profileData.industry || ''}
                        onChange={(e) => setProfileData({...profileData, industry: e.target.value})}
                        className="mt-1 w-full bg-navy border border-white/10 text-white rounded-md px-3 py-2"
                      >
                        <option value="">Select...</option>
                        <option value="technology">Technology</option>
                        <option value="healthcare">Healthcare</option>
                        <option value="finance">Finance</option>
                        <option value="retail">Retail</option>
                        <option value="manufacturing">Manufacturing</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="companySize" className="text-slate">Company Size</Label>
                      <select
                        id="companySize"
                        value={profileData.companySize || ''}
                        onChange={(e) => setProfileData({...profileData, companySize: e.target.value})}
                        className="mt-1 w-full bg-navy border border-white/10 text-white rounded-md px-3 py-2"
                      >
                        <option value="">Select...</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="title" className="text-slate">
                      Professional Title *
                    </Label>
                    <Input
                      id="title"
                      value={profileData.title || ''}
                      onChange={(e) => setProfileData({...profileData, title: e.target.value})}
                      className="mt-1 bg-navy border-white/10 text-white"
                      placeholder="e.g., Fractional CTO"
                    />
                  </div>

                  <div>
                    <Label htmlFor="executiveFunction" className="text-slate">Executive Function</Label>
                    <select
                      id="executiveFunction"
                      value={profileData.executiveFunction || ''}
                      onChange={(e) => setProfileData({...profileData, executiveFunction: e.target.value})}
                      className="mt-1 w-full bg-navy border border-white/10 text-white rounded-md px-3 py-2"
                    >
                      <option value="">Select...</option>
                      <option value="cto">CTO / Technology</option>
                      <option value="cfo">CFO / Finance</option>
                      <option value="cmo">CMO / Marketing</option>
                      <option value="cro">CRO / Revenue</option>
                      <option value="coo">COO / Operations</option>
                      <option value="chro">CHRO / People</option>
                      <option value="other">Other C-Suite</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="yearsOfExperience" className="text-slate">Years Experience</Label>
                      <select
                        id="yearsOfExperience"
                        value={profileData.yearsOfExperience || ''}
                        onChange={(e) => setProfileData({...profileData, yearsOfExperience: e.target.value})}
                        className="mt-1 w-full bg-navy border border-white/10 text-white rounded-md px-3 py-2"
                      >
                        <option value="">Select...</option>
                        <option value="5-10">5-10 years</option>
                        <option value="10-15">10-15 years</option>
                        <option value="15-20">15-20 years</option>
                        <option value="20+">20+ years</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-slate">Hourly Rate Range</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <Input
                          placeholder="Min $"
                          value={profileData.hourlyRateMin || ''}
                          onChange={(e) => setProfileData({...profileData, hourlyRateMin: e.target.value})}
                          className="bg-navy border-white/10 text-white"
                        />
                        <Input
                          placeholder="Max $"
                          value={profileData.hourlyRateMax || ''}
                          onChange={(e) => setProfileData({...profileData, hourlyRateMax: e.target.value})}
                          className="bg-navy border-white/10 text-white"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <Button 
                onClick={handleProfileSubmit}
                disabled={isLoading}
                className="w-full bg-teal hover:bg-teal-light text-navy mt-4"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    Complete Registration
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <button
                onClick={handleSkipProfile}
                disabled={isLoading}
                className="w-full text-slate hover:text-white text-sm py-2"
              >
                Skip for now (limited features)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 4: Success
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-teal/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-teal" />
          </div>

          <h1 className="font-heading text-3xl font-bold text-white mb-4">
            Welcome to CxO On Demand!
          </h1>

          <p className="text-slate mb-8">
            {intent === 'hire' 
              ? 'Your company account is ready. Start hiring top fractional executives.'
              : intent === 'cxo'
              ? 'Your CXO profile is ready. Start receiving brief matches.'
              : 'Your account is ready. Start exploring CXO insights.'}
          </p>

          <div className="bg-navy-light rounded-2xl p-6 border border-white/10 mb-8">
            <h3 className="font-medium text-white mb-4">Quick start guide:</h3>
            <ul className="space-y-3 text-left">
              {intent === 'hire' ? (
                <>
                  <li className="flex items-center gap-3 text-slate">
                    <div className="w-8 h-8 bg-success/20 rounded-lg flex items-center justify-center">
                      <FileText className="w-4 h-4 text-success" />
                    </div>
                    Create your first brief
                  </li>
                  <li className="flex items-center gap-3 text-slate">
                    <div className="w-8 h-8 bg-success/20 rounded-lg flex items-center justify-center">
                      <Search className="w-4 h-4 text-success" />
                    </div>
                    Browse verified CXOs
                  </li>
                  <li className="flex items-center gap-3 text-slate">
                    <div className="w-8 h-8 bg-success/20 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-4 h-4 text-success" />
                    </div>
                    Start an engagement
                  </li>
                </>
              ) : intent === 'cxo' ? (
                <>
                  <li className="flex items-center gap-3 text-slate">
                    <div className="w-8 h-8 bg-teal/20 rounded-lg flex items-center justify-center">
                      <User className="w-4 h-4 text-teal" />
                    </div>
                    Complete your profile
                  </li>
                  <li className="flex items-center gap-3 text-slate">
                    <div className="w-8 h-8 bg-teal/20 rounded-lg flex items-center justify-center">
                      <FileText className="w-4 h-4 text-teal" />
                    </div>
                    Set up your services
                  </li>
                  <li className="flex items-center gap-3 text-slate">
                    <div className="w-8 h-8 bg-teal/20 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-4 h-4 text-teal" />
                    </div>
                    Respond to brief matches
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-3 text-slate">
                    <div className="w-8 h-8 bg-slate/20 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-slate" />
                    </div>
                    Follow CXO thought leaders
                  </li>
                  <li className="flex items-center gap-3 text-slate">
                    <div className="w-8 h-8 bg-slate/20 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-4 h-4 text-slate" />
                    </div>
                    Read curated articles
                  </li>
                  <li className="flex items-center gap-3 text-slate">
                    <div className="w-8 h-8 bg-slate/20 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-4 h-4 text-slate" />
                    </div>
                    Upgrade when ready
                  </li>
                </>
              )}
            </ul>
          </div>

          <Button 
            onClick={() => navigate(intent === 'hire' ? '/company/dashboard' : intent === 'cxo' ? '/cxo/dashboard' : '/member/dashboard')}
            className="w-full bg-teal hover:bg-teal-light text-navy"
          >
            Go to Dashboard
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
